/**
 * What the exceptions board does to the one queue EXP-008 answers with.
 *
 * **The merge and the ranking used to be here and are not any more.** The board read DEL-08 delivery
 * exceptions, LOG-11 logistics exceptions and DSP-05's dispatch read model, normalised three
 * vocabularies into one row, and ranked them — whether a `critical` logistics exception outranks a
 * dispatch search two minutes from its deadline. R-05 in `docs/design/DESIGN_RECONCILIATION.md` is
 * the measurement of why that was the wrong place for it, and this file's own header said so:
 * *"it lives in the console today and belongs in the dispatch BFF when that exists"*. It exists, so
 * the rule moved: `services/experience/dispatch-console-bff/src/domain/exceptions.ts` holds the
 * buckets, the urgency order, the bounded head and the counts, and this file takes `rows`, `counts`
 * and `bucket` as given. R-14 records the move.
 *
 * What is left is what only a screen decides, and it is more than it sounds:
 *
 *   - **The words.** The headline, the line under it, the detail panel's field table and the clock's
 *     caption are the design's own sentences, built from each source's own published fields. A
 *     response cannot carry them without the BFF deciding what an operator reads.
 *   - **The state class.** The badge hue, the glyph, the row tint and the left edge, which is the
 *     design's own `classify()` — a rule about pixels that no contract publishes.
 *   - **The toolbar.** Search, state filter, ordering, paging and export, over the rows the one
 *     request already brought back. R-01 records that no listing on this platform publishes any of
 *     these, and the aggregate publishes none of them either; what makes them work here is that a
 *     triage queue is bounded, which is still the case when something else does the bounding.
 */

/** Which service a row came from. The design shows these as tabs, with counts. */
export type TriageSource = "delivery" | "logistics" | "dispatch";

/**
 * The four buckets the triage tiles count, in the design's own words.
 *
 * `breaching` is first because it is the reason the board exists: a deadline about to pass, a search
 * with no attempts left, or a logistics exception someone has called critical.
 */
export type TriageBucket = "breaching" | "human" | "retry" | "investigating";

/**
 * One line of the detail panel's field table: a field its own contract publishes, and its value.
 *
 * Published fields only. Where the design shows a field no contract publishes — a delivery address as
 * a place name, a dispatch pickup, a logistics subject location — the line is absent rather than
 * filled with a plausible-looking label, because a label an operator can read is a label they will
 * act on.
 */
export interface DetailField {
  /** The field's name exactly as its contract publishes it. Drawn in Geist Mono. */
  readonly name: string;
  readonly value: string;
  /** True for identifiers and codes, which the design draws in Geist Mono. */
  readonly mono?: boolean;
  /**
   * An instant rather than a value: rendered as a countdown against the board's clock.
   *
   * Kept as a flag rather than formatted by whoever builds the row, because the row is built once
   * when the fetch lands and the countdown is redrawn every second after that.
   */
  readonly instant?: boolean;
}

/** A row of the merged queue, as the screen draws it: the aggregate's row plus the design's words. */
export interface TriageRow {
  readonly source: TriageSource;
  /**
   * Which tile this row is counted under, as the aggregate decided it.
   *
   * Taken rather than re-derived. Every console reading this aggregate counts the same four tiles,
   * and the rule — a deadline inside `breachingWithinMs`, an exhausted search, a `critical`
   * severity — is the one R-05 says no single service can publish and two consoles must not each
   * guess at.
   */
  readonly bucket: TriageBucket;
  /** The service code that owns it, so a screen can name what it is calling. */
  readonly code: string;
  readonly id: string;
  /** The status exactly as its own contract publishes it. Never normalised across sources. */
  readonly status: string;
  /** LOG-11 publishes this; the other two do not. */
  readonly severity?: "low" | "medium" | "high" | "critical";
  /** The headline: what happened, in an operator's words. */
  readonly what: string;
  /** The line under it: the contract fields behind the headline. */
  readonly detail: string;
  /** The job identifier an operator recognises. */
  readonly job: string;
  /** Where it is, where published. */
  readonly where?: string;
  /** A deadline or a scheduled retry, whichever the source has. Drives the clock column. */
  readonly deadlineAt?: string;
  /** What the clock is counting: "to deadline", "attempts used", "until retry". */
  readonly clockLabel: string;
  /** Needed by every command, so it travels with the row rather than being fetched again. */
  readonly version: number;
  readonly occurredAt: string;
  /**
   * Which service and aggregate this is, in the words the design's detail panel uses:
   * "DEL-08 delivery exception", "LOG-11 logistics exception", "DSP-05 dispatch operation".
   */
  readonly serviceName: string;
  /** The published fields the detail panel lists, in the design's order. */
  readonly fields: readonly DetailField[];
  /**
   * DEL-08 `evidenceReferences`. References only: the panel never turns one into an image, because
   * FIL-04 secure download is what serves the bytes and this screen is not wired to it.
   */
  readonly evidence?: readonly string[];
  /** The field name this row's `occurredAt` came from, so the timeline can name it honestly. */
  readonly occurredAtField: string;
  /** DSP-05 publishes both; nothing else does. The panel's callout reads them. */
  readonly attempt?: number;
  readonly offersCancelled?: boolean;
}

/**
 * A countdown, `m:ss`, with `+` in front once it has passed.
 *
 * The design's own `fmtClock`, converted from minutes to milliseconds. Not "5m 30s": a clock an
 * operator is watching tick is read as a clock, and `m:ss` is what a clock looks like.
 */
export function formatClock(remainingMs: number): string {
  const seconds = Math.round(Math.abs(remainingMs) / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${remainingMs < 0 ? "+" : ""}${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * What the badge says.
 *
 * The raw status, and the severity after it where the source publishes one — "failed",
 * "finding_candidates", "open · critical". Read off the rendered design, whose own state filter offers
 * exactly these combined values, so the word on the badge and the word in the filter are one string.
 * The class still decides the glyph and the hue.
 */
export function badgeLabel(row: TriageRow): string {
  return row.severity === undefined ? row.status : `${row.status} · ${row.severity}`;
}

/** How long something has been open, in the design's words: "9 min", "1 h 5 min". */
export function formatAge(elapsedMs: number): string {
  const minutes = Math.round(Math.abs(elapsedMs) / 60000);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/**
 * Which state class a row is drawn in: its badge hue and glyph, its row tint and its left edge.
 *
 * This is the design's own `classify()`, and it lives on the screen for the reason the design put it
 * there — two of the three sources need a rule no status-to-class table can express.
 *
 * A dispatch search that is *about to* miss its deadline is drawn as `failed` while its status still
 * says `finding_candidates`, because the thing an operator must act on is the two minutes, not the
 * word. And a logistics exception's class comes from `severity` paired with `status`: LOG-11 publishes
 * three statuses and four severities, and `open · critical` and `open · low` are not the same row.
 *
 * `@tdelivr/design-tokens` has no LOG-11 lifecycle at all, so without this every logistics row would
 * render as the unclassified badge — a dashed grey box with a raw status in it — which is honest about
 * the table and wrong about the row.
 *
 * It took a clock and no longer does. "About to miss its deadline" is `bucket: "breaching"`, which
 * the aggregate decided against `breachingWithinMs` at the instant it assembled the queue — one
 * judgement for the whole board rather than one per row per render, and the same judgement the tile
 * above the table is counting. The screen comparing its own clock to a threshold of its own was the
 * duplicate R-05 is about.
 */
export function stateClassOf(
  row: TriageRow,
): "failed" | "human" | "caution" | "active" | "waiting" {
  if (row.source === "dispatch") {
    return row.status === "failed" || row.bucket === "breaching" ? "failed" : "human";
  }
  if (row.source === "delivery") {
    return row.status === "open" ? "human" : "caution";
  }
  if (row.status === "investigating") return "active";
  return row.severity === "critical"
    ? "failed"
    : row.severity === "high"
      ? "human"
      : row.severity === "medium"
        ? "caution"
        : "waiting";
}

/** What the four tiles say, in the design's wording. */
export const TRIAGE_TILES: readonly {
  readonly bucket: TriageBucket;
  readonly label: string;
  readonly detail: string;
  readonly glyph: string;
}[] = [
  {
    bucket: "breaching",
    label: "Failed or breaching",
    detail: "Deadline under 2 min, exhausted, or critical",
    glyph: "error",
  },
  {
    bucket: "human",
    label: "Needs a human",
    detail: "Open, waiting for a decision",
    glyph: "back_hand",
  },
  {
    bucket: "retry",
    label: "Retry or return",
    detail: "Scheduled, being handled",
    glyph: "event_repeat",
  },
  {
    bucket: "investigating",
    label: "Investigating",
    detail: "Logistics, in progress",
    glyph: "manage_search",
  },
];

/** The source tabs, in the design's order. */
export const TRIAGE_SOURCES: readonly {
  readonly source: TriageSource | "all";
  readonly label: string;
  readonly glyph: string;
}[] = [
  { source: "all", label: "All", glyph: "stacks" },
  { source: "delivery", label: "Delivery", glyph: "local_shipping" },
  { source: "logistics", label: "Logistics", glyph: "inventory_2" },
  { source: "dispatch", label: "Dispatch", glyph: "alt_route" },
];

/**
 * Search across the fetched rows.
 *
 * Client-side, and correct here for the same reason the merge is: the board already holds every row
 * it is going to show. This is not a substitute for a server-side search on the listings that need
 * one — see R-01 in the reconciliation — it is what a bounded triage set makes possible for free.
 */
export function matches(row: TriageRow, term: string): boolean {
  if (term.trim().length === 0) return true;
  const needle = term.trim().toLowerCase();
  return [row.id, row.job, row.what, row.detail, row.where, row.status]
    .filter((value): value is string => typeof value === "string")
    .some((value) => value.toLowerCase().includes(needle));
}

/**
 * The orderings the toolbar's Sort control offers, in the design's own words.
 *
 * `default` is urgency, and it is the default for the reason the board exists. The other three are the
 * design's generic data-grid sorts, present on every grid in the bundle.
 */
export type TriageOrder = "default" | "state" | "az" | "za";

/** State classes worst first, which is the order the badges are drawn in everywhere else. */
const STATE_ORDER = ["failed", "human", "caution", "active", "waiting"] as const;

export interface TriagePage {
  readonly rows: readonly TriageRow[];
  /**
   * Every row that matched, ordered, not just the page.
   *
   * Export takes this: an operator exporting a board wants the queue they are looking at, not the ten
   * rows that happened to be on screen when they clicked.
   */
  readonly all: readonly TriageRow[];
  readonly total: number;
  readonly counts: Readonly<Record<TriageSource | "all", number>>;
  readonly buckets: Readonly<Record<TriageBucket, number>>;
  readonly pageCount: number;
}

/**
 * Everything the board renders, from the queue the aggregate answered with and what the operator has
 * asked for.
 *
 * One function so the counts, the page and the ordering cannot disagree with each other — the tile
 * counts and the tab counts are computed from the same filtered set the table is paged from, rather
 * than each being worked out where it is drawn.
 *
 * **`rows` arrive in urgency order and this does not re-impose one.** `default` is therefore "as the
 * aggregate answered", which is the ordering R-05 says belongs in one place; the three other choices
 * are the design's generic data-grid sorts and each keeps that order inside its own key, because
 * `Array.prototype.sort` is stable. A board that re-ranked the rows here would be the second
 * implementation of the rule, differing from the first the day either changed.
 *
 * The counts are over the *filtered* set rather than the aggregate's own `counts`, and that is the
 * design's behaviour rather than an oversight: the tabs and the tiles answer "how much of what I am
 * looking at", so they move when a filter does. The aggregate's `counts` answer a different question
 * — how much is in the queue — and `countsAreTotals` says whether even that may be drawn as a total.
 */
export function triagePage(
  all: readonly TriageRow[],
  options: {
    readonly source: TriageSource | "all";
    /** A badge label — `open`, `open · critical` — which is what the design's filter offers. */
    readonly status?: string;
    readonly search?: string;
    readonly order?: TriageOrder;
    readonly page: number;
    readonly perPage: number;
  },
): TriagePage {
  const visible = all
    .filter((row) => options.status === undefined || badgeLabel(row) === options.status)
    .filter((row) => matches(row, options.search ?? ""));

  const counts = {
    all: visible.length,
    delivery: visible.filter((row) => row.source === "delivery").length,
    logistics: visible.filter((row) => row.source === "logistics").length,
    dispatch: visible.filter((row) => row.source === "dispatch").length,
  };
  const buckets = {
    breaching: 0,
    human: 0,
    retry: 0,
    investigating: 0,
  };
  for (const row of visible) buckets[row.bucket] += 1;

  const inSource =
    options.source === "all" ? visible : visible.filter((row) => row.source === options.source);
  const ordered = [...inSource].sort((left, right) => {
    switch (options.order ?? "default") {
      case "state":
        return STATE_ORDER.indexOf(stateClassOf(left)) - STATE_ORDER.indexOf(stateClassOf(right));
      case "az":
        return left.what.localeCompare(right.what);
      case "za":
        return right.what.localeCompare(left.what);
      default:
        return 0;
    }
  });
  const from = (options.page - 1) * options.perPage;

  return {
    rows: ordered.slice(from, from + options.perPage),
    all: ordered,
    total: ordered.length,
    counts,
    buckets,
    pageCount: Math.max(1, Math.ceil(ordered.length / options.perPage)),
  };
}
